import { useCallback, useEffect, useMemo, useRef } from "react";

import { useSyncedRef } from "./useSyncedRef";

import type { Key } from "react-aria";
import type { FilterFn } from "../components/combobox/useMultiSelectComboBoxState";

interface BaseProps<T> {
  currentItems: T[];
  selectedKeys: Set<Key>;
  getKey: (item: T) => Key;
}

interface DefaultProps<T> extends BaseProps<T> {
  allowManualUpdate?: false;
}
interface ManualUpdateProps<T> extends BaseProps<T> {
  allowManualUpdate: true;
}

type Props<T> = DefaultProps<T> | ManualUpdateProps<T>;

interface ReturnedFilterProps<T> {
  defaultFilter: FilterFn<T>;
  defaultItems: T[];
}
type ManualUpdateFn<T> = (items: T[]) => void;

/**
 * A hook that maintains a list of items and remembers previously loaded items if they are selected.
 * This is useful for comboboxes with a controlled list of items filtered on the server, where we
 * want to keep previously selected items selected even if they are no longer in the list of items
 * returned by the current query.
 *
 * Without this, you could search for an item, select it, then search for a different item, and the
 * combobox would be unable to display the previously selected item because it is no longer in the
 * list of search results.
 */
export function useFilteredItemsWithMemory<T>(args: DefaultProps<T>): ReturnedFilterProps<T>;
export function useFilteredItemsWithMemory<T>(
  args: ManualUpdateProps<T>
): [ReturnedFilterProps<T>, ManualUpdateFn<T>];
export function useFilteredItemsWithMemory<T>({
  currentItems,
  selectedKeys,
  getKey,
  allowManualUpdate = false,
}: Props<T>) {
  const getKeyRef = useSyncedRef(getKey);

  // Store a mapping of previously loaded items by key
  const previouslyLoadedItems = useRef<Record<Key, T>>({});

  const updatePreviouslyLoadedItems = useCallback(
    (items: T[]) => {
      const newItems = items.reduce(
        (acc, item) => ({
          ...acc,
          [getKeyRef.current(item)]: item,
        }),
        {} as Record<Key, T>
      );
      Object.assign(previouslyLoadedItems.current, newItems);
    },
    [getKeyRef]
  );

  // Whenever we get new items, add them to the map
  useEffect(() => {
    updatePreviouslyLoadedItems(currentItems);
  }, [updatePreviouslyLoadedItems, currentItems]);

  const currentItemKeys = useMemo(
    () => new Set(currentItems.map(getKeyRef.current)),
    [currentItems, getKeyRef]
  );

  // Combine the current items with any previously selected items that are no longer in the list
  const defaultItems = useMemo(() => {
    const previouslySelectedItems = [...selectedKeys]
      .filter(key => !currentItemKeys.has(key))
      .map(key => previouslyLoadedItems.current[key]);

    return [...currentItems, ...previouslySelectedItems];
  }, [currentItemKeys, currentItems, selectedKeys]);

  // Selected items are always included in the list because of the logic above, but we hide them
  // if we are actively filtering and they aren't included in the results
  const defaultFilter: FilterFn<T> = useCallback(
    (node, inputValue) => {
      // If we're not filtering, show all items
      if (!inputValue) {
        return true;
      }
      // Otherwise only show items that actually match the current filter
      return currentItemKeys.has(node.key);
    },
    [currentItemKeys]
  );

  const filterProps = {
    defaultFilter,
    defaultItems,
  };

  if (allowManualUpdate) {
    return [filterProps, updatePreviouslyLoadedItems] as const;
  }

  return filterProps;
}
